import { DecimalPipe, NgIf } from '@angular/common';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { FEEDBACK_INCENTIVE, FEE_PERCENTAGE, MINIMUM_FEE } from '@curbnturf/entities';
import { DisplayValidationErrorsContainerDirective, DisplayValidationErrorsDirective } from '@curbnturf/form-helpers';
import { toPrecision } from '@curbnturf/helpers';
import { Subscription } from 'rxjs';

@Component({
  selector: 'curbnturf-price',
  templateUrl: './price.component.html',
  styleUrls: ['./price.component.scss'],
  standalone: true,
  imports: [
    DecimalPipe,
    DisplayValidationErrorsDirective,
    DisplayValidationErrorsContainerDirective,
    FormsModule,
    NgIf,
    ReactiveFormsModule,
  ],
})
export class PriceComponent implements OnChanges, OnDestroy, OnInit {
  @Input() price: FormControl<number | null>;

  minimumFee = MINIMUM_FEE;
  feePercentage = FEE_PERCENTAGE;
  feedbackIncentive = FEEDBACK_INCENTIVE;

  totalPrice = new FormControl<number | null>(null, [Validators.min(this.minimumFee + this.feedbackIncentive)]);
  updatingPrice: boolean;
  updatingTotalPrice: boolean;

  private subscriptions: Subscription[] = [];
  private formSubscriptions: Subscription[] = [];

  ngOnInit() {
    this.subscriptions.push(
      this.totalPrice.valueChanges.subscribe(() =>
        this.totalPrice?.value && this.totalPrice.value >= this.minimumFee + this.feedbackIncentive
          ? this.totalPriceChange(this.totalPrice.value)
          : null,
      ),
    );
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.price && this.price) {
      this.clearFormSubscriptions();

      this.formSubscriptions.push(
        this.price.valueChanges.subscribe(() =>
          typeof this.price.value === 'number' && this.price.value >= 0 ? this.priceChange(this.price.value) : null,
        ),
      );

      this.priceChange(this.price.value);
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
    this.clearFormSubscriptions();
  }

  clearFormSubscriptions() {
    this.formSubscriptions.forEach((sub) => sub.unsubscribe());
    this.formSubscriptions = [];
  }

  priceChange(value: number | null) {
    if ((value || value === 0) && !this.updatingTotalPrice) {
      const fee = Math.max((value / (1 - this.feePercentage)) * this.feePercentage, this.minimumFee);
      const price = this.toPrice(value + fee + this.feedbackIncentive);

      if (price >= this.minimumFee + this.feedbackIncentive) {
        this.updatingPrice = true;
        this.totalPrice.setValue(price);
        this.updatingPrice = false;
      }
    } else if (value === null) {
      this.updatingPrice = true;
      this.totalPrice.setValue(null);
      this.updatingPrice = false;
    }
  }

  totalPriceChange(value: number) {
    if (!this.updatingPrice) {
      const fee = Math.max((value - this.feedbackIncentive) * this.feePercentage, this.minimumFee);

      this.updatingTotalPrice = true;
      this.price.setValue(this.toPrice(value - fee - this.feedbackIncentive));
      this.price.markAsDirty();
      this.updatingTotalPrice = false;
    }
  }

  toPrice(value: number): number {
    return toPrecision(value, 2);
  }
}
